const DEVELOPMENT_API_URL = "http://localhost:8080";
const PRODUCTION_API_URL = "https://api.africankaratefederation.org";

export function resolveApiBaseUrl(
  configuredUrl: string | undefined,
  isDevelopment: boolean,
): string {
  const defaultUrl = isDevelopment ? DEVELOPMENT_API_URL : PRODUCTION_API_URL;

  return (configuredUrl?.trim() || defaultUrl).replace(/\/+$/, "");
}

const apiBaseUrl = resolveApiBaseUrl(
  import.meta.env["VITE_API_URL"],
  import.meta.env.DEV,
);

type ApiErrorBody = { error?: { code?: string; message?: string } };

export class ApiError extends Error {
  status: number;
  code: string | undefined;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  public?: boolean;
};

export function getAuthToken(): string | null {
  return localStorage.getItem("auth_token");
}

export function setAuthToken(token: string | null) {
  if (token) localStorage.setItem("auth_token", token);
  else localStorage.removeItem("auth_token");
}

export function parseJwt(token: string) {
  try {
    const base64Url = token.split(".")[1]!;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { public: isPublic, body, ...requestOptions } = options;
  const headers = new Headers(requestOptions.headers);
  headers.set("accept", "application/json");
  if (body !== undefined) headers.set("content-type", "application/json");

  if (!isPublic) {
    const token = getAuthToken();
    if (!token) {
      throw new ApiError(
        "Your session has expired. Please sign in again.",
        401,
        "UNAUTHORIZED",
      );
    }
    headers.set("authorization", `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      ...requestOptions,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError(
      "Cannot reach the examination server. Please check your connection.",
      0,
    );
  }

  const payload = (await response.json().catch(() => ({}))) as ApiErrorBody;
  if (!response.ok) {
    throw new ApiError(
      payload.error?.message ?? `Server request failed (${response.status}).`,
      response.status,
      payload.error?.code,
    );
  }
  return payload as T;
}
